import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Baby, Camera, LogOut, Plus, QrCode, ShieldCheck, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GENDER, LEAD, fmtDate } from "@/lib/format";
import { Pill } from "@/components/Common";
import { Avatar, Topo } from "@/components/Brand";
import { QrDialog, WaiverDialog } from "@/components/Dialogs";
import { EMPTY_PROFILE, Field, ProfileFieldsForm } from "@/components/ProfileFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { compressSquareImage } from "@/lib/image";

function AvatarUpload({ p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const save = async (image) => {
    setBusy(true);
    try {
      await api.put(`/me/profile/${p.id}/avatar`, { image });
      toast.success(image ? "Foto profil diperbarui." : "Foto profil dihapus.");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const pick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar.");
    try {
      save(await compressSquareImage(file, 320, 0.75));
    } catch {
      toast.error("Gambar tidak bisa diproses.");
    }
  };
  return (
    <div className="flex shrink-0 flex-col items-center gap-1">
      <label className="group relative cursor-pointer" aria-label="Ganti foto profil" data-testid="profile-avatar-upload">
        <Avatar name={p.full_name} src={p.avatar} size={72} className="ring-4 ring-[#3D6F4B]" testId="profile-avatar" />
        <span className="absolute -bottom-0.5 -right-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#B9D3A8] text-[#1A3323] ring-2 ring-[#1A3323] transition-transform group-hover:scale-110">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
        </span>
        <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="profile-avatar-input" />
      </label>
      {p.avatar && <button onClick={() => save(null)} disabled={busy} className="text-[10px] font-semibold text-[#FDFCF8]/60 hover:text-[#FDFCF8]" data-testid="profile-avatar-remove">hapus foto</button>}
    </div>
  );
}

function AddChildDialog({ open, onOpenChange, onDone }) {
  const [v, setV] = useState(EMPTY_PROFILE);
  const [w, setW] = useState(null);
  const [agree, setAgree] = useState(false);
  const [child, setChild] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) api.get("/auth/waiver").then((r) => setW(r.data));
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!v.gender) return toast.error("Pilih jenis kelamin.");
    setBusy(true);
    try {
      await api.post("/me/children", { ...v, accept_waiver: agree, waiver_version: w?.waiver?.version, child_confirmed: child });
      toast.success("Profil anak ditambahkan.");
      setV(EMPTY_PROFILE);
      setAgree(false);
      setChild(false);
      onDone();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="add-child-dialog">
        <DialogHeader>
          <DialogTitle>Tambah profil anak</DialogTitle>
          <DialogDescription>Untuk peserta di bawah {w?.minor_age_threshold ?? 17} tahun. Tiket & riwayat tercatat atas nama anak.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <ProfileFieldsForm v={v} set={setV} prefix="child" />
          {w?.waiver && <div className="max-h-32 overflow-y-auto whitespace-pre-line rounded-lg bg-[#F5F3EB] p-3 text-xs">{w.waiver.content}</div>}
          <label className="flex items-start gap-2 text-sm"><Checkbox checked={agree} onCheckedChange={(x) => setAgree(!!x)} data-testid="child-waiver-guardian-checkbox" /> Saya, sebagai wali, menyetujui waiver untuk anak ini.</label>
          {w?.require_child_confirmation && (
            <label className="flex items-start gap-2 text-sm"><Checkbox checked={child} onCheckedChange={(x) => setChild(!!x)} data-testid="child-waiver-child-checkbox" /> Anak menyatakan memahami aturan keselamatan.</label>
          )}
          <Button type="submit" disabled={busy || !agree} className="h-12 w-full rounded-full font-bold" data-testid="add-child-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan profil anak
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteAccountDialog({ open, onOpenChange, onDeleted }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/me/account/delete", { pin });
      toast.success("Akun kamu sudah dihapus.");
      onDeleted();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setPin(""); onOpenChange(o); }}>
      <DialogContent className="max-w-sm" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-700">Hapus akun permanen?</DialogTitle>
          <DialogDescription>Semua data akun ini, termasuk profil anak, riwayat climbing, tiket, dan poin leaderboard akan dihapus total dan tidak bisa dikembalikan.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Masukkan PIN untuk konfirmasi">
            <Input type="password" inputMode="numeric" maxLength={6} required className="h-11 tracking-widest" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="delete-account-pin-input" />
          </Field>
          <Button type="submit" variant="destructive" disabled={busy || pin.length !== 6} className="h-12 w-full rounded-xl font-bold" data-testid="delete-account-confirm-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Hapus akun saya
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const editable = (p) => ({ display_name: p.display_name || "", city: p.city, emergency_phone: p.emergency_phone || "", community: p.community || "", email: p.email || "" });

function EditProfile({ p, onSaved }) {
  const [v, setV] = useState(editable(p));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setV(editable(p));
  }, [p]);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...v };
      if (p.is_child) delete payload.email;
      await api.put(`/me/profile/${p.id}`, payload);
      toast.success("Profil disimpan.");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-2xl bg-white p-4 ring-1 ring-[#1A3323]/10">
      <Field label="Nama tampilan (leaderboard)"><Input value={v.display_name} onChange={(e) => setV({ ...v, display_name: e.target.value })} data-testid="profile-display-name-input" /></Field>
      <Field label="Nama Komunitas Kamu" hint="Muncul di bawah namamu di leaderboard."><Input maxLength={40} value={v.community} onChange={(e) => setV({ ...v, community: e.target.value })} data-testid="profile-community-input" /></Field>
      {!p.is_child && <Field label="Email"><Input type="email" required value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} data-testid="profile-email-input" /></Field>}
      <Field label="Kota domisili"><Input value={v.city} onChange={(e) => setV({ ...v, city: e.target.value })} data-testid="profile-city-input" /></Field>
      <Field label="No. telepon darurat"><Input value={v.emergency_phone} onChange={(e) => setV({ ...v, emergency_phone: e.target.value })} data-testid="profile-emergency-input" /></Field>
      <Button onClick={save} disabled={busy} className="h-11 w-full rounded-full" data-testid="profile-save-button">Simpan</Button>
    </div>
  );
}

export default function Profile() {
  const { user, profiles, activeProfile, selectProfile, refresh, signOut } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qr, setQr] = useState(false);
  const [addChild, setAddChild] = useState(false);
  const [waiver, setWaiver] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const load = () => activeProfile && api.get(`/me/profile/${activeProfile.id}`).then((r) => setP(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile?.id]);
  const openWaiver = async () => setWaiver((await api.get("/auth/waiver")).data.waiver);
  const logout = async () => {
    await signOut();
    nav("/", { replace: true });
  };
  if (!p) return null;
  const lead = LEAD[p.lead_status] || LEAD.NOT_VERIFIED;
  return (
    <div className="space-y-5">
      <div className="grain relative overflow-hidden rounded-[26px] bg-[#1A3323] p-6 text-[#FDFCF8] rise">
        <Topo className="absolute -right-20 -top-10 h-56 w-80 text-white/10" />
        <div className="relative flex items-center gap-4">
          <AvatarUpload p={p} onChanged={() => { load(); refresh(); }} />
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#FDFCF8]/60">{p.is_child ? "Climber cilik" : "Climber"}</div>
            <h1 className="text-2xl font-extrabold leading-tight" data-testid="profile-full-name">{p.full_name}</h1>
            {p.community && <p className="text-sm font-semibold text-[#B9D3A8]" data-testid="profile-community">{p.community}</p>}
            <p className="text-xs text-[#FDFCF8]/70" data-testid="profile-member-since">Member sejak {fmtDate(p.created_at)}</p>
          </div>
        </div>
        <div className="relative mt-5 grid grid-cols-3 gap-2 text-center">
          {[["Total climb", p.completed, "profile-total-climbs"], ["Sesi hadir", p.attendance, "profile-attendance"], ["Peringkat", p.rank ? `#${p.rank}` : "—", "profile-rank"]].map(([l, v, id]) => (
            <div key={l} className="rounded-2xl bg-white/10 py-3"><div className="font-mono text-2xl font-extrabold" data-testid={id}>{v}</div><div className="text-[11px] text-[#FDFCF8]/65">{l}</div></div>
          ))}
        </div>
        <p className="relative mt-4 text-sm text-[#FDFCF8]/70">{GENDER[p.gender]} · {p.age} th · {p.city} · lahir {fmtDate(p.dob)}</p>
        <div className="relative mt-4 flex flex-wrap gap-2">
          <Pill className={lead.cls} testid="profile-lead-status">Lead: {lead.label}</Pill>
          <Pill className={p.waiver_ok ? "bg-green-100 text-green-900" : "bg-red-100 text-red-900"} testid="profile-waiver-status">Waiver: {p.waiver_ok ? "OK" : "Perlu disetujui"}</Pill>
          <Pill className="relative bg-white/15 text-white"><Link to="/app/riwayat" data-testid="profile-history-link">Lihat riwayat climbing →</Link></Pill>
        </div>
        <div className="relative mt-4 flex gap-2">
          <Button variant="secondary" className="rounded-full" onClick={() => setQr(true)} data-testid="profile-show-qr-button"><QrCode className="mr-1.5 h-4 w-4" /> QR Saya</Button>
          {!p.waiver_ok && <Button variant="secondary" className="rounded-full" onClick={openWaiver} data-testid="profile-open-waiver-button"><ShieldCheck className="mr-1.5 h-4 w-4" /> Setujui Waiver</Button>}
        </div>
      </div>
      <EditProfile p={p} onSaved={() => { load(); refresh(); }} />
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-[#1A3323]">Profil dalam akun ini</h2>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAddChild(true)} data-testid="add-child-button"><Plus className="mr-1 h-4 w-4" /> Anak</Button>
        </div>
        <p className="text-xs text-muted-foreground">Akun: {user?.phone}. Sebagai wali, kamu bisa berpindah profil lewat menu di atas.</p>
        {profiles.map((x) => (
          <button key={x.id} onClick={() => selectProfile(x.id)} data-testid={`profile-card-${x.id}`}
            className={`flex w-full items-center gap-3 rounded-2xl p-3 text-left ring-1 transition-colors ${x.id === activeProfile?.id ? "bg-[#1A3323]/5 ring-[#1A3323]" : "bg-white ring-[#1A3323]/10 hover:bg-[#EDE9DC]"}`}>
            <Baby className={`h-5 w-5 ${x.is_child ? "text-[#3D6F4B]" : "text-muted-foreground"}`} />
            <div className="flex-1"><div className="font-bold text-[#1A3323]">{x.full_name}</div><div className="text-xs text-muted-foreground">{x.is_child ? "Anak" : "Saya"} · {x.age} th</div></div>
            {x.id === activeProfile?.id && <Pill className="bg-[#1A3323] text-white">Aktif</Pill>}
          </button>
        ))}
      </section>
      <Button className="h-14 w-full rounded-full bg-[#1A3323] text-base font-extrabold text-[#FDFCF8] shadow-lg shadow-[#1A3323]/20 transition-transform hover:-translate-y-0.5 hover:bg-[#24452F]" onClick={logout} data-testid="logout-button"><LogOut className="mr-2 h-5 w-5" /> Keluar</Button>
      <section className="!mt-16 rounded-2xl border border-red-200 bg-red-50/60 p-4" data-testid="danger-zone">
        <h2 className="text-sm font-extrabold text-red-800">Zona berbahaya</h2>
        <p className="mt-1 text-xs text-red-900/70">Menghapus akun akan menghilangkan semua data secara permanen, termasuk profil anak, riwayat, dan poin leaderboard.</p>
        <Button variant="outline" size="sm" onClick={() => setDeleting(true)} className="mt-3 rounded-full border-red-300 bg-white text-red-700 hover:bg-red-100 hover:text-red-800" data-testid="delete-account-button"><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Hapus akun permanen</Button>
      </section>
      <DeleteAccountDialog open={deleting} onOpenChange={setDeleting} onDeleted={logout} />
      <QrDialog open={qr} onOpenChange={setQr} profileId={p.id} />
      <AddChildDialog open={addChild} onOpenChange={setAddChild} onDone={refresh} />
      <WaiverDialog open={!!waiver} onOpenChange={(o) => !o && setWaiver(null)} waiver={waiver} profile={p} onAccepted={() => { load(); refresh(); }} />
    </div>
  );
}
